import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, ButtonGroup, Form, Badge } from 'react-bootstrap';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import API from '../services/api';
import {
  money, pct, COLORS, PALETTE, chartMoneyOptions, Kpi, ChartCard, STATUS_LABEL, TIPO_LABEL, METHOD_LABEL, monthLabel, moneyShort,
} from './adminShared';
import { formatDay } from '../utils/dates';
import Loading from './Loading';

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const PRESETS = {
  today: { label: 'Hoy', range: () => { const d = new Date(); return [ymd(d), ymd(d)]; } },
  month: { label: 'Este mes', range: () => { const d = new Date(); return [ymd(new Date(d.getFullYear(), d.getMonth(), 1)), ymd(d)]; } },
  lastMonth: { label: 'Mes pasado', range: () => { const d = new Date(); return [ymd(new Date(d.getFullYear(), d.getMonth() - 1, 1)), ymd(new Date(d.getFullYear(), d.getMonth(), 0))]; } },
  d30: { label: '30 días', range: () => { const d = new Date(); const s = new Date(d); s.setDate(s.getDate() - 29); return [ymd(s), ymd(d)]; } },
  quarter: { label: 'Trimestre', range: () => { const d = new Date(); return [ymd(new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1)), ymd(d)]; } },
  year: { label: 'Este año', range: () => { const d = new Date(); return [ymd(new Date(d.getFullYear(), 0, 1)), ymd(d)]; } },
};

const AdminOverviewTab = () => {
  const [preset, setPreset] = useState('month');
  const [[from, to], setRange] = useState(PRESETS.month.range());
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    API.get('/admin/overview', { params: { from, to } })
      .then(res => { if (!cancelled) { setData(res.data); setError(''); } })
      .catch(err => { if (!cancelled) setError(err.response?.data?.message || 'No se pudo cargar el resumen.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to]);

  const choose = (key) => { setPreset(key); setRange(PRESETS[key].range()); };

  const charts = useMemo(() => {
    if (!data) return null;
    const { charts: c, snapshot: s } = data;
    const buckets = s.receivables.buckets;
    return {
      trend: {
        labels: c.monthly.labels.map(monthLabel),
        datasets: [
          { type: 'line', label: 'Utilidad neta', data: c.monthly.netProfit, borderColor: COLORS.amber, backgroundColor: COLORS.amber, tension: 0.3, pointRadius: 3, order: 0 },
          { label: 'Ventas', data: c.monthly.sales, backgroundColor: 'rgba(18,184,178,0.65)', borderRadius: 4, order: 1 },
          { label: 'Cobrado', data: c.monthly.collected, backgroundColor: 'rgba(74,222,128,0.55)', borderRadius: 4, order: 1 },
          { label: 'Gastos', data: c.monthly.expenses, backgroundColor: 'rgba(248,113,113,0.55)', borderRadius: 4, order: 1 },
        ],
      },
      daily: {
        labels: c.daily.labels.map(l => (c.daily.granularity === 'month' ? monthLabel(l) : `${l.slice(8, 10)}/${l.slice(5, 7)}`)),
        datasets: [
          { label: 'Ventas', data: c.daily.sales, borderColor: COLORS.cyan, backgroundColor: 'rgba(18,184,178,0.15)', fill: true, tension: 0.3, pointRadius: 2 },
          { label: 'Cobrado', data: c.daily.collected, borderColor: COLORS.green, backgroundColor: COLORS.green, tension: 0.3, pointRadius: 2 },
        ],
      },
      aging: {
        labels: ['Al día', '1–30 días', '31–60 días', '61–90 días', '+90 días'],
        datasets: [{ data: [buckets.current, buckets.d1_30, buckets.d31_60, buckets.d61_90, buckets.d90plus], backgroundColor: [COLORS.green, COLORS.blue, COLORS.amber, '#FB923C', COLORS.red], borderRadius: 4 }],
      },
      tipo: {
        labels: c.byTipo.map(t => TIPO_LABEL[t.tipoEcf] || t.tipoEcf),
        datasets: [{ data: c.byTipo.map(t => t.total), backgroundColor: c.byTipo.map(t => (t.total < 0 ? COLORS.red : COLORS.cyan)), borderRadius: 4 }],
      },
      methods: { labels: c.paymentMethods.map(m => METHOD_LABEL[m.method] || m.method), datasets: [{ data: c.paymentMethods.map(m => m.total), backgroundColor: PALETTE, borderWidth: 0 }] },
      categories: { labels: c.expensesByCategory.map(m => m.category), datasets: [{ data: c.expensesByCategory.map(m => m.total), backgroundColor: PALETTE, borderWidth: 0 }] },
      clients: { labels: c.topClients.map(m => m.name), datasets: [{ data: c.topClients.map(m => m.total), backgroundColor: COLORS.cyan, borderRadius: 4 }] },
      products: { labels: c.topProducts.map(m => m.name), datasets: [{ data: c.topProducts.map(m => m.revenue), backgroundColor: COLORS.blue, borderRadius: 4 }] },
      users: { labels: c.byUser.map(m => m.username), datasets: [{ data: c.byUser.map(m => m.total), backgroundColor: COLORS.purple, borderRadius: 4 }] },
      status: { labels: s.invoiceStatus.map(m => STATUS_LABEL[m.status] || m.status), datasets: [{ data: s.invoiceStatus.map(m => m.count), backgroundColor: PALETTE, borderWidth: 0 }] },
    };
  }, [data]);

  if (error && !data) return <div className="alert alert-danger">{error}</div>;
  if (!data) return <Loading label="Cargando resumen..." />;

  const { current: c, previous: p, snapshot: s, charts: ch } = data;
  const horizontal = chartMoneyOptions({ indexAxis: 'y', scales: { x: { ticks: { callback: (v) => moneyShort(v) } } } });
  const doughnut = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' } } };
  const lowRange = s.compliance.ranges.filter(r => r.remaining <= Math.max(10, Math.ceil(r.total * 0.1)));

  return (
    <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <ButtonGroup size="sm">
          {Object.entries(PRESETS).map(([key, { label }]) => (
            <Button key={key} variant={preset === key ? 'primary' : 'outline-secondary'} onClick={() => choose(key)}>{label}</Button>
          ))}
        </ButtonGroup>
        <Form.Control size="sm" type="date" style={{ width: 150 }} value={from} max={to}
          onChange={e => { setPreset('custom'); setRange([e.target.value, to]); }} />
        <span className="text-muted small">a</span>
        <Form.Control size="sm" type="date" style={{ width: 150 }} value={to} min={from}
          onChange={e => { setPreset('custom'); setRange([from, e.target.value]); }} />
        <span className="text-muted small ms-auto">
          Comparado con {formatDay(data.range.previousFrom)} – {formatDay(data.range.previousTo)}
        </span>
      </div>

      <div className="text-muted small mb-2 text-uppercase" style={{ letterSpacing: '.06em' }}>Dinero</div>
      <Row className="g-3 mb-3">
        <Col md={3} sm={6}><Kpi label="Ventas netas" value={money(c.netSales)} current={c.netSales} previous={p.netSales} sub={`Facturado ${money(c.grossInvoiced)}`} hint="Facturas + notas de débito − notas de crédito aceptadas por DGII" /></Col>
        <Col md={3} sm={6}><Kpi label="Cobrado" value={money(c.collected)} current={c.collected} previous={p.collected} sub={`Tasa de cobro ${pct(c.collectionRate)}`} hint="Pagos recibidos en el período" /></Col>
        <Col md={3} sm={6}><Kpi label="Cuentas por cobrar" value={money(s.receivables.total)} inverse tone={s.receivables.overdue > 0 ? 'amber' : undefined} sub={`Vencido ${money(s.receivables.overdue)} (${pct(s.receivables.overduePct)})`} hint="Saldo pendiente hoy" /></Col>
        <Col md={3} sm={6}><Kpi label="Utilidad neta" value={money(c.netProfit)} current={c.netProfit} previous={p.netProfit} tone={c.netProfit < 0 ? 'red' : undefined} sub={`Margen neto ${pct(c.netMarginPct)}`} hint="Ventas − costo de lo vendido − gastos (simplificado: usa el costo actual de cada producto)" /></Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col md={3} sm={6}><Kpi label="Utilidad bruta" value={money(c.grossProfit)} current={c.grossProfit} previous={p.grossProfit} sub={`Margen bruto ${pct(c.grossMarginPct)}`} /></Col>
        <Col md={3} sm={6}><Kpi label="Gastos" value={money(c.expenses)} current={c.expenses} previous={p.expenses} inverse /></Col>
        <Col md={3} sm={6}><Kpi label="ITBIS del período" value={money(c.itbis)} current={c.itbis} previous={p.itbis} hint="ITBIS de las ventas netas (a declarar)" /></Col>
        <Col md={3} sm={6}><Kpi label="Días de cobro (DSO)" value={s.receivables.dso === null ? '—' : `${Math.round(s.receivables.dso)} días`} hint="Cuentas por cobrar ÷ ventas de los últimos 90 días × 90" sub="Cuanto menor, mejor" /></Col>
      </Row>
      <Row className="g-3 mb-4">
        <Col md={3} sm={6}><Kpi label="Facturas emitidas" value={c.invoiceCount} current={c.invoiceCount} previous={p.invoiceCount} sub={`Ticket promedio ${c.avgInvoice === null ? '—' : money(c.avgInvoice)}`} /></Col>
        <Col md={3} sm={6}><Kpi label="Notas de crédito" value={money(c.creditNotes)} current={c.creditNotes} previous={p.creditNotes} inverse sub={`${c.creditNoteCount} nota(s) · ${pct(c.creditNoteRatePct)} de lo facturado`} tone={c.creditNoteRatePct > 5 ? 'amber' : undefined} /></Col>
        <Col md={3} sm={6}><Kpi label="Concentración de clientes" value={pct(s.concentration.topClientPct)} inverse sub={`Top 3 clientes: ${pct(s.concentration.top3Pct)} de las ventas`} tone={s.concentration.topClientPct > 40 ? 'amber' : undefined} hint="Qué parte de las ventas depende de un solo cliente" /></Col>
        <Col md={3} sm={6}><Kpi label="Clientes" value={s.clients.total} sub={`${s.clients.newInPeriod} nuevo(s) · cotizaciones: ${s.quotes.count} (${pct(s.quotes.conversionPct)} aceptadas)`} /></Col>
      </Row>

      <div className="text-muted small mb-2 text-uppercase" style={{ letterSpacing: '.06em' }}>Cumplimiento DGII</div>
      <Row className="g-3 mb-4">
        <Col md={3} sm={6}><Kpi label="Esperando confirmación DGII" value={s.compliance.pendingDgii} tone={s.compliance.pendingDgii > 0 ? 'amber' : undefined} /></Col>
        <Col md={3} sm={6}><Kpi label="Rechazadas (30 días)" value={s.compliance.rejectedLast30Days} tone={s.compliance.rejectedLast30Days > 0 ? 'red' : undefined} sub="e-NCF ya consumido: hay que emitir otra" /></Col>
        <Col md={3} sm={6}><Kpi label="Sin enviar a DGII" value={s.compliance.notSent} tone={s.compliance.over60hUnsent > 0 ? 'red' : s.compliance.staleOver24h > 0 ? 'amber' : undefined} sub={`${s.compliance.staleOver24h} con más de 24 h · límite legal 72 h`} /></Col>
        <Col md={3} sm={6}>
          <Kpi label="Rangos de e-NCF" value={s.compliance.ranges.length ? `${s.compliance.ranges.length} activos` : 'Sandbox'} tone={lowRange.length ? 'amber' : undefined}
            sub={s.compliance.ranges.length
              ? s.compliance.ranges.map(r => `${r.tipoEcf}: quedan ${r.remaining}`).join(' · ')
              : 'Sin rangos autorizados en este ambiente'} />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col lg={7}>
          <ChartCard title="Tendencia de 12 meses" subtitle="Ventas, cobros, gastos y utilidad neta">
            <Bar data={charts.trend} options={{ ...chartMoneyOptions(), plugins: { legend: { display: true }, tooltip: chartMoneyOptions().plugins.tooltip } }} />
          </ChartCard>
        </Col>
        <Col lg={5}>
          <ChartCard title="Ventas vs. cobros del período" subtitle={ch.daily.granularity === 'month' ? 'Por mes' : 'Por día'} empty={ch.daily.sales.every(v => !v) && ch.daily.collected.every(v => !v)}>
            <Line data={charts.daily} options={{ ...chartMoneyOptions(), plugins: { legend: { display: true }, tooltip: chartMoneyOptions().plugins.tooltip } }} />
          </ChartCard>
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col lg={4}>
          <ChartCard title="Antigüedad de cuentas por cobrar" subtitle="Saldo pendiente según vencimiento" empty={s.receivables.total === 0}>
            <Bar data={charts.aging} options={chartMoneyOptions()} />
          </ChartCard>
        </Col>
        <Col lg={4}>
          <ChartCard title="Ventas por tipo de comprobante" empty={ch.byTipo.length === 0}>
            <Bar data={charts.tipo} options={horizontal} />
          </ChartCard>
        </Col>
        <Col lg={4}>
          <ChartCard title="Estado de las facturas" subtitle="Todas las facturas del sistema" empty={s.invoiceStatus.length === 0}>
            <Doughnut data={charts.status} options={doughnut} />
          </ChartCard>
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col lg={4}>
          <ChartCard title="Mejores clientes" subtitle="Ventas netas del período" empty={ch.topClients.length === 0}>
            <Bar data={charts.clients} options={horizontal} />
          </ChartCard>
        </Col>
        <Col lg={4}>
          <ChartCard title="Productos que más venden" subtitle="Ingreso del período" empty={ch.topProducts.length === 0}>
            <Bar data={charts.products} options={horizontal} />
          </ChartCard>
        </Col>
        <Col lg={4}>
          <ChartCard title="Ventas por usuario" subtitle="Quién factura" empty={ch.byUser.length === 0}>
            <Bar data={charts.users} options={horizontal} />
          </ChartCard>
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col lg={4}>
          <ChartCard title="Cómo se cobra" subtitle="Métodos de pago del período" empty={ch.paymentMethods.length === 0}>
            <Doughnut data={charts.methods} options={doughnut} />
          </ChartCard>
        </Col>
        <Col lg={4}>
          <ChartCard title="En qué se gasta" subtitle="Gastos por categoría" empty={ch.expensesByCategory.length === 0}>
            <Doughnut data={charts.categories} options={doughnut} />
          </ChartCard>
        </Col>
        <Col lg={4}>
          <ChartCard title="Quién nos debe más" subtitle="Saldos pendientes por cliente" empty={s.receivables.topDebtors.length === 0}>
            <Bar
              data={{ labels: s.receivables.topDebtors.map(d => d.name), datasets: [{ data: s.receivables.topDebtors.map(d => d.owed), backgroundColor: COLORS.amber, borderRadius: 4 }] }}
              options={horizontal}
            />
          </ChartCard>
        </Col>
      </Row>
      {s.compliance.ranges.length > 0 && (
        <div className="small text-muted">
          {s.compliance.ranges.map(r => (
            <Badge key={r.tipoEcf} bg="secondary" className="me-2">{r.tipoEcf}: {r.used}/{r.total} usados{r.expiration ? ` · vence ${r.expiration}` : ''}</Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOverviewTab;
